import { FC, useMemo, useEffect, useState } from 'react';
import {
  FieldsRow,
  FormTextField,
  FieldsSection,
  FormCheckbox,
} from '../../../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { Stack, Button, Typography, Alert, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { DeactivationPopup } from '../deactivation/deactivation.popup.tsx';
import { IPopupWrapper } from '../../../../../../../../shared/components/popup/components/popup.component.tsx';
import LinkIcon from '@mui/icons-material/Link';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConnectedCustomersPopup } from '../connectedCustomers/connectedCustomers.popup.tsx';
import { bakery2bClientsState } from '../../../../../states/bakery2bClients.state.ts';
import { IFormData } from './clientDetails.popup.tsx';
import { useBeforeClosePopup } from '../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import { filter } from 'rxjs';
import { ContentLoading } from '../../../../../../../../shared/components/loading/loading.component.tsx';

export const Content: FC<IContent> = ({ field, stream }) => {
  const { t } = useTranslation();
  const {
    watch,
    reset,
    formState: { isDirty, errors },
    handleSubmit,
  } = useFormContext<IFormData>();
  const deactivationPopup = useMemo(() => new DeactivationPopup(), []);
  const connectedCustomersPopup = useMemo(() => new ConnectedCustomersPopup(), []);
  const [loading, setLoading] = useState(false);
  const [reinviteProcess, setReinviteProcess] = useState(false);

  const onSubmit = (data: IFormData) => {
    bakery2bClientsState.pub.saveBakery2bClient(data);
    stream.emit('close');
  };
  useEffect(() => {
    const bakery2bClientsStateSub = bakery2bClientsState.sub
      .state()
      .pipe(
        filter(
          ({ action }) =>
            action === 'deactivateBakery2bClient' || action === 'activateBakery2bClient',
        ),
      )
      .subscribe(({ selectedBakery2bClient }) => {
        if (selectedBakery2bClient) {
          reset(selectedBakery2bClient);
        }
      });
    const clientDetailsLoadingSub = bakery2bClientsState.sub
      .clientDetailsLoading()
      .subscribe(setLoading);
    const reinviteProcessSub = bakery2bClientsState.sub
      .reinviteProcess()
      .subscribe(setReinviteProcess);
    return () => {
      bakery2bClientsStateSub.unsubscribe();
      clientDetailsLoadingSub.unsubscribe();
      reinviteProcessSub.unsubscribe();
    };
  }, []);
  useBeforeClosePopup({
    stream,
    isDirty,
  });
  return (
    <ContentLoading {...{ loading }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        {watch('isDeactivated') && (
          <FieldsRow>
            <Alert severity='warning' sx={{ width: '100%' }}>
              <b>{t('settings.deactivate_message')}</b>
              <br />
              {t('common.reason')}: {watch('whyDeactivated')}
            </Alert>
          </FieldsRow>
        )}
        <FieldsRow paddingTop='8px' sx={{ gap: 1 }}>
          <FormTextField label={t('common.email')} disabled name='email' />
          <FormTextField
            label={t('common.name')}
            name='name'
            error={Boolean(errors['name'])}
            helperText={errors['name'] ? t(String(errors['name'].message)) : ''}
            autoFocus={'name' === field}
          />
        </FieldsRow>
        <FieldsRow>
          <Stack
            sx={{
              width: '100%',
            }}
          >
            <FormTextField
              autoFocus={'note' === field}
              label={t('common.note')}
              name='note'
              width='100%'
              multiline
              minRows={3}
              maxRows={5}
              slotProps={{ htmlInput: { maxLength: 128 } }}
            />
            <Typography
              sx={{
                textAlign: 'right',
                mt: '2px',
                mr: '2px',
                fontSize: 14,
                color: 'text.secondary',
              }}
            >
              {t('settings.left_symbols')}: {128 - (watch('note')?.length || 0)}
            </Typography>
          </Stack>
        </FieldsRow>
        <FieldsSection titleBoxSx={{ pt: 0 }} title={t('common.rights')}>
          <Stack
            sx={{
              paddingLeft: 1,
            }}
          >
            <FormCheckbox name='orderAddNewItem' label={t('settings.add_new_articles')} />
            <FormCheckbox
              name='orderNoDelivery'
              label={t('settings.order_no_delivery_on_this_day')}
            />
            <FormCheckbox name='orderShowPrice' label={t('settings.order_show_price_total')} />
            <FormCheckbox name='standardOrderEdit' label={t('settings.standard_order_edit')} />
          </Stack>
        </FieldsSection>
        <FieldsSection
          titleBoxSx={{ pt: 0 }}
          title={t('settings.more_settings')}
          contentBoxSx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
        >
          <Button
            variant='outlined'
            size='small'
            startIcon={<LinkIcon />}
            onClick={() => {
              connectedCustomersPopup.stream.emit('open');
            }}
          >
            <SButtonText>{t('settings.connected_customers')}</SButtonText>
          </Button>
          {watch('isDeactivated') ? (
            <>
              <Button
                onClick={() => bakery2bClientsState.pub.activateBakery2bClient()}
                color='success'
                variant='outlined'
                startIcon={<CheckCircleIcon />}
              >
                <SButtonText>{t('settings.activate_client')}</SButtonText>
              </Button>
              <Button
                onClick={() => bakery2bClientsState.pub.deleteBakery2bClient()}
                color='error'
                variant='outlined'
                startIcon={<DeleteIcon />}
              >
                <SButtonText>{t('settings.delete_client')}</SButtonText>
              </Button>
            </>
          ) : (
            <Button
              variant='outlined'
              color='error'
              onClick={() => deactivationPopup.stream.emit('open')}
              startIcon={<DoDisturbOnIcon />}
            >
              <SButtonText>{t('settings.deactivate_client')}</SButtonText>
            </Button>
          )}
          <Button
            variant='outlined'
            disabled={reinviteProcess}
            onClick={() => bakery2bClientsState.pub.reinviteBakery2bClient()}
            startIcon={<NotificationsActiveIcon />}
          >
            <SButtonText>{t('settings.invitation_reminder')}</SButtonText>
          </Button>
        </FieldsSection>
        <Stack
          direction='row'
          sx={{
            justifyContent: 'flex-end',
            mt: watch('isDeactivated') ? 3 : 10,
          }}
        >
          <Button variant='contained' sx={{ minWidth: '125px' }} type='submit' disabled={!isDirty}>
            <SButtonText>{t('common.save')}</SButtonText>
          </Button>
        </Stack>
        <deactivationPopup.Component />
        <connectedCustomersPopup.Component />
      </form>
    </ContentLoading>
  );
};

interface IContent {
  field: string;
  stream: IPopupWrapper['stream'];
}

const SButtonText = styled('span')({
  whiteSpace: 'nowrap',
});
